/**
 * Reglas de cantidad de venta — peso / volumen / unidad.
 * Fuente: sm_products.min_qty + qty_step (multiplicador).
 */

export type SaleQtyProductLike = {
  tipoVenta?: string | null;
  unidadMedida?: string | null;
  minQty?: number | null;
  qtyStep?: number | null;
};

export type SaleQtyRules = {
  min: number;
  step: number;
  unitLabel: string;
  isFractional: boolean;
};

function roundQty(n: number, step: number): number {
  if (!Number.isFinite(n) || step <= 0) return n;
  const decimals = String(step).includes(".") ? (String(step).split(".")[1]?.length ?? 0) : 0;
  const factor = 10 ** Math.min(decimals, 4);
  return Math.round(n * factor) / factor;
}

/** Unidades de masa/volumen — no deben etiquetar productos vendidos por unidad. */
const MASS_VOLUME_UNITS = new Set([
  "kg", "g", "mg", "lb", "oz", "ml", "l", "lt", "litro", "litros",
]);

function defaultStepForUnit(unidad: string | null | undefined, tipoVenta: string): number {
  if (tipoVenta !== "peso") return 1;
  const u = (unidad ?? "kg").toLowerCase();
  if (u === "g" || u === "ml") return 50;
  if (u === "l" || u === "lt" || u === "litro" || u === "litros") return 0.25;
  return 0.5; // kg y demás → permite 1.5
}

function resolveUnitLabel(tipo: "peso" | "unidad", unidadMedida: string | null | undefined): string {
  const raw = (unidadMedida && String(unidadMedida).trim()) || "";
  if (tipo === "peso") return raw || "kg";
  // Venta por unidad: ignorar kg/g residuales de seeds/demos (merchant-demo).
  if (!raw || MASS_VOLUME_UNITS.has(raw.toLowerCase())) return "u";
  return raw;
}

export function getSaleQtyRules(p: SaleQtyProductLike): SaleQtyRules {
  const tipo = p.tipoVenta === "peso" ? "peso" : "unidad";
  const unitLabel = resolveUnitLabel(tipo, p.unidadMedida);
  const stepRaw = p.qtyStep != null && Number(p.qtyStep) > 0 ? Number(p.qtyStep) : defaultStepForUnit(p.unidadMedida, tipo);
  const minRaw = p.minQty != null && Number(p.minQty) > 0 ? Number(p.minQty) : stepRaw;
  const step = roundQty(stepRaw, stepRaw);
  const min = roundQty(Math.max(minRaw, step), step);
  return {
    min,
    step,
    unitLabel,
    isFractional: tipo === "peso" || step < 1,
  };
}

/** Primera cantidad al agregar al carrito. */
export function initialSaleQty(p: SaleQtyProductLike): number {
  return getSaleQtyRules(p).min;
}

/** Aplica delta (+/−). `delta` = ±1 (botón genérico) o ±step explícito. */
export function applySaleQtyDelta(current: number, delta: number, p: SaleQtyProductLike): number {
  const { min, step } = getSaleQtyRules(p);
  let applied: number;
  if (Math.abs(Math.abs(delta) - step) < 1e-9) {
    applied = delta < 0 ? -step : step;
  } else if (delta === 1 || delta === -1) {
    applied = delta * step;
  } else {
    const steps = Math.max(1, Math.round(Math.abs(delta)));
    applied = (delta < 0 ? -1 : 1) * step * steps;
  }
  const next = roundQty(current + applied, step);
  if (next < min - 1e-9) return 0;
  return next;
}

/** ¿qty es ≥ min y (casi) múltiplo de step desde min? */
export function isValidSaleQty(qty: number, p: SaleQtyProductLike): boolean {
  const { min, step } = getSaleQtyRules(p);
  if (!Number.isFinite(qty) || qty <= 0) return false;
  if (qty + 1e-9 < min) return false;
  const stepsFromMin = (qty - min) / step;
  return Math.abs(stepsFromMin - Math.round(stepsFromMin)) < 1e-6;
}

export function formatSaleQty(qty: number, p: SaleQtyProductLike): string {
  const { unitLabel, isFractional } = getSaleQtyRules(p);
  const n = isFractional
    ? qty.toLocaleString("es-AR", { maximumFractionDigits: 3 })
    : String(Math.round(qty));
  return `${n} ${unitLabel}`;
}
